import type { DatadogRum, RumInitConfiguration } from '@datadog/browser-rum';

import {
    captureForegroundTimingStart,
    completeForegroundDuration,
    getForegroundPerfNow,
    type ForegroundDuration,
    type ForegroundTimingStart,
} from 'lib/telemetry/foregroundTime';

/**
 * Lazy facade over `@datadog/browser-rum`.
 *
 * The SDK (~1 MB) used to be pulled into the first-load bundle of every route
 * via a top-level `import { datadogRum } from '@datadog/browser-rum'` in the
 * always-loaded module graph (root store, transport monitors, error
 * boundaries). Initialization was already deferred to idle, but the static
 * import forced the code into first-load regardless.
 *
 * This module exposes the exact `datadogRum.*` surface the app uses, but the
 * real SDK is dynamic-`import()`ed only when `init()` runs (driven by
 * `initDatadog` on idle). Calls made before the SDK loads are buffered
 * and flushed in order once it's ready — equivalent to the real SDK's own
 * pre-`init` buffering, since nothing reaches Datadog before `init` anyway.
 *
 * Drop-in: importers do `import * as datadogRum from 'lib/telemetry/datadogRum'`
 * so every existing `datadogRum.addError(...)` / `datadogRum.addAction(...)`
 * call site is unchanged.
 */

type Rum = DatadogRum;

let rum: Rum | null = null;
let loadPromise: Promise<Rum | null> | null = null;

// Buffer pre-init fire-and-forget calls. Capped so that environments where the
// SDK never loads (non-prod) don't accumulate ops unboundedly.
const queue: Array<(r: Rum) => void> = [];
const MAX_QUEUE = 200;

function enqueue(op: (r: Rum) => void): void {
    if (rum) {
        op(rum);
        return;
    }
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push(op);
}

/**
 * Dynamic-import the SDK, run the real `init`, then flush buffered calls.
 * Idempotent. Fire-and-forget — ordering is preserved because buffered ops
 * always run after `datadogRum.init` inside the resolved import.
 *
 * `onReady` runs on the real SDK immediately after `datadogRum.init()` and
 * BEFORE the buffered queue drains. The Next.js plugin puts the SDK in manual
 * view mode (no current view until the first `startView`), so the initial view
 * must be started here rather than enqueued — otherwise buffered `setViewName`/
 * `addTiming` calls made before init (e.g. a page's `setViewName('event-page')`)
 * would drain first, land in "no view", and be lost.
 */
export function init(config: RumInitConfiguration, onReady?: (rum: Rum) => void): void {
    if (loadPromise) return;

    loadPromise = import('@datadog/browser-rum')
        .then(({ datadogRum }) => {
            datadogRum.init(config);
            rum = datadogRum;
            // Isolated from the queue drain: an `onReady` throw would otherwise land in
            // the `.catch` below, which clears `queue` — losing every buffered op over a
            // failure in an unrelated callback.
            try {
                onReady?.(datadogRum);
            } catch (err: unknown) {
                console.warn('Datadog RUM onReady failed', err);
            }
            for (const op of queue.splice(0)) op(datadogRum);
            return datadogRum;
        })
        .catch((err: unknown) => {
            // Offline / ad-blocked / chunk load failure. Telemetry is
            // best-effort — drop buffered ops and stay a no-op.
            queue.length = 0;
            console.warn('Datadog RUM failed to load', err);
            return null;
        });
}

export function setUser(...args: Parameters<Rum['setUser']>): void {
    enqueue((r) => r.setUser(...args));
}

export function clearUser(): void {
    enqueue((r) => r.clearUser());
}

export function setGlobalContextProperty(...args: Parameters<Rum['setGlobalContextProperty']>): void {
    enqueue((r) => r.setGlobalContextProperty(...args));
}

export function addError(...args: Parameters<Rum['addError']>): void {
    enqueue((r) => r.addError(...args));
}

export function addAction(...args: Parameters<Rum['addAction']>): void {
    enqueue((r) => r.addAction(...args));
}

// Monotonic counter of pathname-changing navigations, used to bind a buffered
// timing to the exact view it was measured on. Datadog starts a new RUM view on
// each pathname change (query-/hash-only changes stay the same view), so a bump
// here mirrors a RUM view boundary. Path equality alone is insufficient: a user
// who leaves and returns to the same pathname before the SDK flushes is on a new
// view, yet the paths match — the epoch distinguishes the two.
let navEpoch = 0;
let navTrackingInstalled = false;
let lastTrackedPathname: string | undefined;

// Callbacks run while the OUTGOING view is still current. See `onBeforeViewChange`.
const beforeViewChangeCallbacks = new Set<() => void>();
// See `onViewChangeReverted`.
const viewChangeRevertedCallbacks = new Set<() => void>();

function runBeforeViewChange(): void {
    for (const cb of beforeViewChangeCallbacks) {
        try {
            cb();
        } catch (err: unknown) {
            console.warn('Datadog RUM beforeViewChange callback failed', err);
        }
    }
}

function runViewChangeReverted(): void {
    for (const cb of viewChangeRevertedCallbacks) {
        try {
            cb();
        } catch (err: unknown) {
            console.warn('Datadog RUM viewChangeReverted callback failed', err);
        }
    }
}

// `performance.now()` at which the current view began. 0 for the initial view, whose
// start is the document's own time origin.
let viewStartPerfNow = 0;

// Same boundary expressed on the foreground-only clock. Keeping the raw and foreground
// anchors together lets elapsed vitals subtract tab-hidden time without losing the
// router-transition work that happened while the destination was visible.
let viewStartForegroundPerfNow = 0;

// When an App Router transition began, if one is in flight. See `markViewTransitionStart`.
let pendingTransitionStartPerfNow: number | undefined;
/**
 * Where that transition said it was headed, when the router told us (it does — see
 * `markViewTransitionStart`).
 *
 * Used for SAME-PATH writes only. Those are a crowd this module can't identify — nuqs
 * rewriting query params on the page being navigated away from looks exactly like a route
 * navigation that only changes the query — so the one thing that separates them is whether
 * the transition was headed here. Pathname-CHANGING writes are identified by ownership
 * instead (see `writeOwnedByApp`), because a route can redirect and the pathname that lands
 * is then not the one named here.
 */
let pendingTransitionTargetPathname: string | undefined;
let pendingTransitionStartForegroundPerfNow: number | undefined;

/**
 * Record that an App Router client navigation has started.
 *
 * The router mutates `history` only once the destination's chunks and data have
 * resolved, so the history write is the END of a transition, not its start. Taking the
 * view start from that moment excludes exactly the work an SPA navigation spends before
 * anything of the new page exists — which is most of what makes a slow one slow, and
 * would leave `chart_ready` reporting near-zero for the navigations worth investigating.
 *
 * Called from the `onRouterTransitionStart` hook re-exported by
 * `src/instrumentation-client.ts`, which fires while `window.location` still points at
 * the previous page.
 */
export function markViewTransitionStart(targetUrl?: string | URL): void {
    installNavTracking();
    const transitionStartPerfNow = performance.now();
    pendingTransitionStartPerfNow = transitionStartPerfNow;
    // `window.location` still points at the previous page here, so the destination has to
    // come from the router. Only same-path writes are judged against it; undefined when a
    // caller doesn't know it, and then any same-path write settles this transition.
    pendingTransitionTargetPathname = targetUrl === undefined ? undefined : targetPathnameOf(targetUrl);
    pendingTransitionStartForegroundPerfNow = getForegroundPerfNow(transitionStartPerfNow);
    for (const cb of viewTransitionStartCallbacks) {
        try {
            cb();
        } catch (err: unknown) {
            console.warn('Datadog RUM viewTransitionStart callback failed', err);
        }
    }
}

/**
 * Register a callback for the start of an App Router transition — the point from which
 * the incoming view's work begins, well before the history write that ends it.
 *
 * Paired with `onViewTransitionAborted`: a transition that settles without changing the
 * pathname (the event page rewrites its query constantly) never becomes a view, so
 * anything a subscriber split off at the start has to be folded back.
 */
export function onViewTransitionStart(callback: () => void): () => void {
    installNavTracking();
    viewTransitionStartCallbacks.add(callback);
    return () => viewTransitionStartCallbacks.delete(callback);
}

/** See `onViewTransitionStart`. Fires when a transition ends without a view change. */
export function onViewTransitionAborted(callback: () => void): () => void {
    installNavTracking();
    viewTransitionAbortedCallbacks.add(callback);
    return () => viewTransitionAbortedCallbacks.delete(callback);
}

const viewTransitionStartCallbacks = new Set<() => void>();
const viewTransitionAbortedCallbacks = new Set<() => void>();

/**
 * @param settlesPendingTransition Whether the write that just landed is the pending
 * transition's own commit. A history write is NOT proof that it is: the outgoing page stays
 * interactive while a route change is in flight and writes urls of its own — `pushEventUrl`
 * pushing an event URL, nuqs rewriting query params. Only the caller knows what was written,
 * so it decides; a transition that did not settle keeps its stamp for the commit still to
 * come, and this navigation starts its view where it actually happened.
 */
function bumpNavEpochOnPathChange(settlesPendingTransition: boolean): void {
    const path = window.location.pathname;
    const hadPendingTransition = pendingTransitionStartPerfNow !== undefined;
    const settles = hadPendingTransition && settlesPendingTransition;
    if (path !== lastTrackedPathname) {
        lastTrackedPathname = path;
        navEpoch += 1;
        const navigationPerfNow = performance.now();
        // The transition's own start when this is its commit. Otherwise now: a navigation
        // with no router signal of its own (popstate, a bare history write from app code) or
        // someone else's url landing while a route change is still in flight — taking the
        // route's stamp there would backdate this view to a click that was never for it.
        const transitionStartPerfNow = settles ? pendingTransitionStartPerfNow : undefined;
        const transitionStartForegroundPerfNow = settles ? pendingTransitionStartForegroundPerfNow : undefined;
        viewStartPerfNow = transitionStartPerfNow ?? navigationPerfNow;
        viewStartForegroundPerfNow = transitionStartForegroundPerfNow ?? getForegroundPerfNow(navigationPerfNow);
    } else if (settles) {
        // The transition ended where it began — a same-path navigation is not a RUM view
        // boundary, so whatever a subscriber split off has to be folded back.
        //
        // KNOWN EDGE: a transition that REDIRECTS onto the path already showing (clicking a
        // link to `/indices` while on `/indices/kpow`, which is where it redirects to) does
        // not match the destination it announced, so it is not recognized here and its stamp
        // survives to backdate whatever navigates next. Ownership can't rescue this branch —
        // a nuqs query write is indistinguishable from a same-path route commit — and one
        // backdated view start in that flow is not worth a mechanism that could mistake
        // every nuqs write for a navigation.
        for (const cb of viewTransitionAbortedCallbacks) {
            try {
                cb();
            } catch (err: unknown) {
                console.warn('Datadog RUM viewTransitionAborted callback failed', err);
            }
        }
    }
    // A transition still in flight keeps its stamp: it belongs to a commit that hasn't
    // happened yet, and its destination view would otherwise be measured from that late
    // write rather than from the click that started it.
    if (hadPendingTransition && !settles) return;
    // Otherwise cleared whether or not the path changed: once its own write lands, the
    // transition is over. Leaving it set would let a query-only write's stale timestamp
    // become the start of some later, unrelated view.
    pendingTransitionStartPerfNow = undefined;
    pendingTransitionStartForegroundPerfNow = undefined;
    pendingTransitionTargetPathname = undefined;
}

/**
 * Depth of history writes that APP code owns, as opposed to the App Router committing a
 * navigation. Nesting is allowed (a write from inside a subscriber's callback), hence a
 * depth rather than a flag.
 */
let appOwnedWriteDepth = 0;

/**
 * Run a history write that app code owns.
 *
 * The wrapper below has to tell those apart from the router's own navigation commits, and
 * cannot do it by looking at the url: a route can redirect (`/indices` → `/indices/kpow`,
 * and the markets routes canonicalize), so the pathname that lands is not always the one
 * the router announced. Matching on it would read a redirected commit as somebody else's
 * write. Marking the app's own writes inverts that — anything unmarked that moves the
 * pathname is the router's, redirects included.
 *
 * Only pathname-CHANGING writes need this: `pushEventUrl` is the only app code that pushes
 * one (everything else replaces the query on the page it is already on), so an unmarked
 * pathname change can be nothing else. Same-path writes are a crowd we don't control —
 * nuqs above all — and are told apart by destination instead.
 */
export function writeOwnedByApp<T>(write: () => T): T {
    appOwnedWriteDepth += 1;
    try {
        return write();
    } finally {
        appOwnedWriteDepth -= 1;
    }
}

/** Whether a same-path write is the pending transition's own commit — i.e. it was headed here. */
function settlesPendingTransition(targetPathname: string | undefined): boolean {
    return pendingTransitionTargetPathname === undefined || pendingTransitionTargetPathname === targetPathname;
}

/**
 * `performance.now()` at which the current RUM view started — 0 on the initial (hard)
 * navigation, and on each SPA navigation the moment the router transition BEGAN (not the
 * later history mutation; see `markViewTransitionStart`).
 *
 * Duration vitals measured from the document's time origin would be correct on a direct
 * load and badly inflated on every client-side navigation (they'd include all previous
 * pages). Subtract this to get a view-relative duration.
 */
export function getCurrentViewStartPerfNow(): number {
    installNavTracking();
    return viewStartPerfNow;
}

/** Foreground-aware timing anchor for the current view. */
export function getCurrentViewTimingStart(): ForegroundTimingStart {
    installNavTracking();
    return { perfNow: viewStartPerfNow, foregroundPerfNow: viewStartForegroundPerfNow };
}

export {
    captureForegroundTimingStart,
    completeForegroundDuration,
    type ForegroundDuration,
    type ForegroundTimingStart,
};

/**
 * Register a callback that runs immediately BEFORE a pathname-changing navigation
 * commits — i.e. while the outgoing RUM view is still the current one.
 *
 * `setViewContextProperty` writes to whichever view is current at call time, and an
 * ended view can never be amended. Anything that accumulates over a view's lifetime
 * (see `mainThreadBlocking`) must therefore publish its final value here; a callback
 * fired *after* the navigation would write the outgoing view's tail onto the incoming
 * view, silently corrupting both.
 *
 * Fires ONLY for pathname changes, matching Datadog's own view boundaries. The event
 * page rewrites the URL constantly without leaving the view (nuqs `sortMarkets`, the
 * order-panel params, ref params); treating those as view ends would reset subscribers'
 * accumulators mid-view, so a single view would both lose the blocking time collected
 * before the query change and emit a `view_blocking_time` vital per query write.
 */
export function onBeforeViewChange(callback: () => void): () => void {
    installNavTracking();
    beforeViewChangeCallbacks.add(callback);
    return () => beforeViewChangeCallbacks.delete(callback);
}

/**
 * Register a callback for the case where an `onBeforeViewChange` firing turned out to be
 * premature: the pathname-changing history write it preceded threw (WebKit rate-limits
 * History mutations), so the URL never moved and no view boundary happened.
 *
 * `onBeforeViewChange` has to fire BEFORE the write — an ended view can never be amended —
 * which means it is necessarily speculative. This is the retraction. It fires synchronously
 * from the failed write, immediately after the premature callback, so a subscriber can undo
 * whatever it closed off (see `mainThreadBlocking`); without it, everything measured after
 * the failure would be charged to an incoming view that never came in.
 */
export function onViewChangeReverted(callback: () => void): () => void {
    installNavTracking();
    viewChangeRevertedCallbacks.add(callback);
    return () => viewChangeRevertedCallbacks.delete(callback);
}

// Installed lazily (only once a timing is actually buffered) so importers that
// never buffer pay no cost. `history.pushState`/`replaceState` don't emit an
// event, so we wrap them (composably — original is called untouched) alongside
// `popstate` to observe SPA navigations.
function installNavTracking(): void {
    if (navTrackingInstalled || typeof window === 'undefined') return;
    navTrackingInstalled = true;
    lastTrackedPathname = window.location.pathname;
    // `popstate` fires *after* the history entry has changed, so this is the earliest
    // observable point — the outgoing view is already gone for URL purposes, but RUM
    // has not started the new view yet (it reacts to the same event), so a flush here
    // still lands on the outgoing view. The pathname has already moved, so compare it
    // against the last tracked one rather than against `window.location`.
    window.addEventListener('popstate', () => {
        if (window.location.pathname !== lastTrackedPathname) runBeforeViewChange();
        // Never app code — a popstate is the browser navigating, whether or not the router
        // announced it as a `traverse`. Either it commits the pending transition or it
        // supersedes it; both mean the transition is over.
        bumpNavEpochOnPathChange(true);
    });
    (['pushState', 'replaceState'] as const).forEach((method) => {
        const original = window.history[method];
        window.history[method] = function (this: History, ...methodArgs: Parameters<History[typeof method]>): void {
            // Before `original.apply`: the outgoing view is unambiguously current here, so
            // the destination has to be read from the pending arguments — and resolved now,
            // since a relative url would resolve against the NEW location afterwards.
            const targetPathname = targetPathnameOf(methodArgs[2]);
            // An unresolvable url counts as a pathname change: a spurious view end splits
            // one view's data, but a missed one loses it entirely.
            const changesPathname = targetPathname === undefined || targetPathname !== window.location.pathname;
            // A pathname change that app code did not mark as its own is the router's, wherever
            // it landed. A same-path write is judged on where the transition was headed.
            const isPendingTransitionCommit = changesPathname
                ? appOwnedWriteDepth === 0
                : settlesPendingTransition(targetPathname);
            if (changesPathname) runBeforeViewChange();
            try {
                original.apply(this, methodArgs);
            } catch (err: unknown) {
                // WebKit rate-limits History mutations and throws a SecurityError past ~100
                // writes per 10s; that error must reach the caller untouched. The URL didn't
                // move, so there is no epoch to bump — the only question is the pending
                // transition stamp, and it depends on WHICH write failed:
                //
                //  - The pending transition's OWN commit settled (unsuccessfully), so clear
                //    the stamp; left set, it would backdate whichever unrelated view
                //    navigates next.
                //  - Any other write is a different URL owner racing a route change that is
                //    still in flight — `pushEventUrl` writing an event URL from the
                //    still-interactive outgoing page. Settling here would report that live
                //    transition as aborted and re-measure the destination view from its own
                //    late commit, so leave it alone. Only a pathname-changing write reaches
                //    this branch, so the two are told apart by ownership: the app marks its
                //    own (see `writeOwnedByApp`), and anything unmarked is the router's.
                //
                // The retraction is unconditional either way: `runBeforeViewChange()` above
                // fired on the promise of THIS write, whoever owned it, and the
                // aborted-transition callback alone can't undo a view its subscribers have
                // already closed off.
                if (changesPathname) {
                    runViewChangeReverted();
                    // Only the transition's own failed commit settles it. The url did not
                    // move, so `bumpNavEpochOnPathChange` sees no path change and just
                    // retires the stamp — which is the point: left set, it would backdate
                    // whichever unrelated view navigates next.
                    if (isPendingTransitionCommit) bumpNavEpochOnPathChange(true);
                }
                throw err;
            }
            bumpNavEpochOnPathChange(isPendingTransitionCommit);
        } as History[typeof method];
    });
}

/**
 * The pathname a pending `pushState`/`replaceState` would land on, or `undefined` when the
 * url can't be resolved.
 *
 * `url` may be absent (a state-only write, which stays exactly where it is), relative, or
 * absolute; resolving against the current href handles all three.
 */
function targetPathnameOf(url: string | URL | null | undefined): string | undefined {
    if (url === undefined || url === null) return window.location.pathname;
    try {
        return new URL(url, window.location.href).pathname;
    } catch {
        return undefined;
    }
}

/**
 * Report on the view that is current *now*, even if the SDK hasn't loaded yet.
 *
 * Datadog attaches custom timings, view context and vitals to whichever RUM view is
 * current at call time. When the SDK hasn't loaded this call is buffered and flushed
 * later — potentially after the user has navigated — which would misattribute the
 * measurement onto the wrong page. If the SDK is ready the current view is correct, so
 * report immediately. Otherwise capture the navigation epoch now and drop the op on
 * replay if any pathname-changing navigation happened before the SDK loaded.
 *
 * Note this drops only on *navigation*, not on "the SDK's first view hadn't started
 * yet": a call made before the plugin's initial `startView` still belongs to that first
 * view, and `init`'s `onReady` starts it before the queue drains.
 */
function reportOnCurrentView(op: (r: Rum) => void): void {
    if (rum) {
        op(rum);
        return;
    }
    installNavTracking();
    const navEpochAtReport = navEpoch;
    enqueue((r) => {
        if (navEpoch !== navEpochAtReport) return;
        op(r);
    });
}

/**
 * Convert a `performance.now()` reading to the Unix-epoch milliseconds that
 * `addDurationVital` expects for `startTime`.
 *
 * The SDK documents `AddDurationVitalOptions.startTime` as "a UNIX timestamp in
 * milliseconds"; passing a raw `performance.now()` value (milliseconds since page
 * load) dates the vital to 1970 and it is silently discarded or misplaced. Durations
 * stay in `performance.now()` space — only the start stamp is converted.
 */
export function epochFromPerfNow(perfNow: number): number {
    return performance.timeOrigin + perfNow;
}

/**
 * Opaque token identifying the current RUM view (bumped on each pathname-changing
 * navigation, matching Datadog's view boundaries). Callers use it to record a
 * timing at most once per view — Datadog keeps custom timings by name on the
 * current view, so two reporters in the same view would otherwise overwrite each
 * other. Stable pre- and post-SDK-load since it derives only from navigations.
 */
export function getCurrentViewToken(): number {
    installNavTracking();
    return navEpoch;
}

export function addTiming(...args: Parameters<Rum['addTiming']>): void {
    reportOnCurrentView((r) => r.addTiming(...args));
}

/**
 * Per-view context. Unlike `setGlobalContextProperty`, this is scoped to the current
 * RUM view and does not follow the user to the next page — the right home for
 * page-shape attributes (see `eventPageRumContext`).
 */
export function setViewContextProperty(...args: Parameters<Rum['setViewContextProperty']>): void {
    reportOnCurrentView((r) => r.setViewContextProperty(...args));
}

/**
 * `options.startTime` must be Unix-epoch milliseconds — build it with
 * `epochFromPerfNow`, never a raw `performance.now()`.
 *
 * Only the completed form is exposed. `startDurationVital`/`stopDurationVital` keep
 * their in-flight state inside the SDK, which does not exist during the buffered
 * pre-init window, so a vital started before load would be lost on `stop`.
 */
export function addDurationVital(...args: Parameters<Rum['addDurationVital']>): void {
    reportOnCurrentView((r) => r.addDurationVital(...args));
}

export function setViewName(...args: Parameters<Rum['setViewName']>): void {
    enqueue((r) => r.setViewName(...args));
}

// No `startView` passthrough on purpose. The initial view is started from `init`'s
// `onReady` hook (see `initDatadog`), which runs before the queue drains. An enqueued
// startView would instead replay *after* the buffered `setViewName`/`addTiming` ops and
// split the view they belong to.

export function startSessionReplayRecording(...args: Parameters<Rum['startSessionReplayRecording']>): void {
    enqueue((r) => r.startSessionReplayRecording(...args));
}

/**
 * Synchronous read. Returns `undefined` when the SDK has not loaded/initialized
 * yet — callers already treat that as "RUM unavailable" (see `getRumContext`).
 */
export function getInternalContext(
    ...args: Parameters<Rum['getInternalContext']>
): ReturnType<Rum['getInternalContext']> | undefined {
    return rum ? rum.getInternalContext(...args) : undefined;
}
